import { Droplets, LayoutGrid, RefreshCw, Table2 } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatHoraValue } from '@/lib/utils';

import { MonitoreoNav } from '../components/monitoreo-nav';
import { capasTanque, colorProducto, galones, medida, mensajeControlador, tanqueOffline } from '../lib/monitoreo-live';
import { useTanksStatus } from '../monitoreo.api';
import type { PaqueteTanque, TanqueCatalogo } from '../monitoreo.types';

type Vista = 'tarjetas' | 'tabla';

/** Gráfico de barras del tanque: capa de producto y capa de agua. */
function CapasTanque({ llenado, agua, producto, color }: { llenado: number; agua: number; producto: number; color: string }) {
  return (
    <div className="flex h-16 items-end gap-2">
      <div className="relative h-full w-16 overflow-hidden rounded-control border border-ink-200 bg-ink-50">
        <div
          className="absolute inset-x-0 bottom-0 bg-sky-300/80"
          style={{ height: `${agua}%` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-x-0 bottom-0 opacity-90"
          style={{ height: `${producto}%`, backgroundColor: color }}
          aria-hidden="true"
        />
      </div>

      <div className="space-y-0.5 text-xs text-ink-600">
        <p className="font-bold text-ink-900">{medida(llenado)} %</p>
        <p>Producto {medida(producto)} %</p>
        {agua > 0 ? <p className="text-sky-700">Agua {medida(agua)} %</p> : null}
      </div>
    </div>
  );
}

/** Tarjeta de un tanque con su telemetría. */
function TarjetaTanque({ paquete, tanque }: { paquete: PaqueteTanque; tanque: TanqueCatalogo | null }) {
  const datos = paquete.Data;
  const offline = tanqueOffline(datos);
  const capacidad = tanque?.capacidad ?? 0;
  const capas = capasTanque(datos, capacidad);
  const color = colorProducto(tanque?.color ?? null);

  const metricas: { label: string; valor: string }[] = [
    { label: 'Galonaje', valor: `${galones(datos?.ProductVolume)} gal` },
    { label: 'Varillaje', valor: `${medida(datos?.ProductHeight)} mm` },
    { label: 'Volumen TC', valor: `${galones(datos?.ProductTCVolume)} gal` },
    { label: 'Disponibilidad', valor: `${galones(datos?.ProductUllage)} gal` },
    { label: 'Altura de agua', valor: `${medida(datos?.WaterHeight)} mm` },
    { label: 'Volumen de agua', valor: `${galones(datos?.WaterVolume)} gal` },
    { label: 'Temperatura', valor: `${medida(datos?.Temperature)} °C` },
    { label: 'Densidad', valor: `${medida(datos?.ProductDensity)} kg/m³` },
    { label: 'Masa', valor: `${medida(datos?.ProductMass)} kg` },
  ];

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-ink-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
          <span className="truncate text-sm font-bold text-ink-900">
            Tanque {tanque?.id_tank ?? paquete.Id}
          </span>
          <span className="truncate text-xs text-ink-500">{tanque?.producto ?? 'Sin producto'}</span>
        </div>

        <Badge variant={offline ? 'danger' : 'success'}>{offline ? 'Desconectado' : 'En línea'}</Badge>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-3">
        <CapasTanque llenado={capas.llenado} agua={capas.agua} producto={capas.producto} color={color} />

        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          {metricas.map((metrica) => (
            <div key={metrica.label}>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                {metrica.label}
              </dt>
              <dd className={cn('tabular-nums', offline ? 'text-ink-400' : 'text-ink-900')}>
                {metrica.valor}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-auto text-xs text-ink-500">
          {datos?.DateTime
            ? `Última medición: ${formatHoraValue(datos.DateTime)}`
            : 'Sin medición registrada en el controlador'}
          {capacidad > 0 ? ` · Capacidad ${galones(capacidad)} gal` : null}
        </p>
      </div>
    </Card>
  );
}

/**
 * Tanques en vivo (`/monitoreo/tanques`).
 *
 * Puerto de `Monitoring::tanks()` y de la pantalla de telemetría del panel
 * anterior: una tarjeta por sonda con el producto, el agua y las medidas del
 * controlador, refrescadas cada 5 segundos.
 */
export function TanqueVivoPage() {
  useDocumentTitle('Monitoreo · Tanques en vivo');

  const [vista, setVista] = useState<Vista>('tarjetas');
  const estado = useTanksStatus();

  const paquetes = estado.data?.packets ?? [];
  const tanques = estado.data?.tanques ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Droplets className="size-6 text-brand-500" aria-hidden="true" />
            Tanques en vivo
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Telemetría de las sondas del controlador: producto, agua, temperatura, densidad y
            disponibilidad de cada tanque. Se actualiza cada 5 segundos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-control border border-ink-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => setVista('tarjetas')}
              aria-pressed={vista === 'tarjetas'}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 text-xs font-semibold transition-colors',
                vista === 'tarjetas' ? 'bg-ink-900 text-white' : 'text-ink-600 hover:text-ink-900',
              )}
            >
              <LayoutGrid className="size-3.5" aria-hidden="true" />
              Tarjetas
            </button>
            <button
              type="button"
              onClick={() => setVista('tabla')}
              aria-pressed={vista === 'tabla'}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 text-xs font-semibold transition-colors',
                vista === 'tabla' ? 'bg-ink-900 text-white' : 'text-ink-600 hover:text-ink-900',
              )}
            >
              <Table2 className="size-3.5" aria-hidden="true" />
              Tabla
            </button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void estado.refetch()}
            loading={estado.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>
        </div>
      </header>

      <MonitoreoNav active="tanques" />

      {estado.data?.actualizado ? (
        <p className="text-xs text-ink-500">
          Última lectura del controlador: {formatHoraValue(estado.data.actualizado)}
        </p>
      ) : null}

      {estado.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((clave) => (
            <Skeleton key={clave} className="h-80 rounded-card" />
          ))}
        </div>
      ) : estado.isError ? (
        <Alert variant="error" title="No se pudo consultar los tanques">
          <div className="flex flex-wrap items-center gap-3">
            <span>{mensajeControlador(estado.error)}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void estado.refetch()}
              loading={estado.isFetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : paquetes.length === 0 ? (
        <Alert variant="warning" title="Sin tanques configurados">
          El controlador no devolvió mediciones. Verifica que las sondas estén configuradas.
        </Alert>
      ) : vista === 'tarjetas' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {paquetes.map((paquete) => (
            <TarjetaTanque
              key={paquete.Id}
              paquete={paquete}
              tanque={tanques.find((item) => item.id_tank === paquete.Id) ?? null}
            />
          ))}
        </div>
      ) : (
        <SectionCard
          title="Telemetría de los tanques"
          description="Medidas reportadas por las sondas del controlador."
        >
          <div className="overflow-x-auto">
            <div className="min-w-[72rem]">
              <div className="grid grid-cols-[6rem_minmax(0,1fr)_8rem_11rem_9rem_8rem_10rem_7.5rem_9rem] items-center gap-x-3 border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                <span>Tanque</span>
                <span>Producto</span>
                <span>Estado</span>
                <span className="text-right">Llenado</span>
                <span className="text-right">Volumen</span>
                <span className="text-right">Varillaje</span>
                <span className="text-right">Disponibilidad</span>
                <span className="text-right">Temperatura</span>
                <span className="text-right">Altura de agua</span>
              </div>

              {paquetes.map((paquete) => {
                const datos = paquete.Data;
                const tanque = tanques.find((item) => item.id_tank === paquete.Id) ?? null;
                const offline = tanqueOffline(datos);
                const llenado = capasTanque(datos, tanque?.capacidad ?? 0).llenado;
                const alturaAgua = Number(datos?.WaterHeight ?? 0);

                return (
                  <div
                    key={paquete.Id}
                    className="grid grid-cols-[6rem_minmax(0,1fr)_8rem_11rem_9rem_8rem_10rem_7.5rem_9rem] items-center gap-x-3 border-b border-ink-50 py-2 text-sm text-ink-800"
                  >
                    <span className="font-semibold">#{tanque?.id_tank ?? paquete.Id}</span>
                    <span
                      className="truncate font-medium"
                      style={{ color: colorProducto(tanque?.color ?? null, '#0f172a') }}
                    >
                      {tanque?.producto ?? 'No relacionado'}
                    </span>
                    <span>
                      <Badge variant={offline ? 'danger' : 'success'}>
                        {offline ? 'Desconectado' : 'En línea'}
                      </Badge>
                    </span>
                    <span className="flex items-center justify-end gap-2">
                      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-ink-100">
                        <span
                          className="block h-full rounded-full"
                          style={{
                            width: `${Math.min(Math.max(llenado, 0), 100)}%`,
                            backgroundColor: colorProducto(tanque?.color ?? null),
                          }}
                        />
                      </span>
                      <span className="tabular-nums">{medida(llenado)} %</span>
                    </span>
                    <span className="text-right tabular-nums">{galones(datos?.ProductVolume)} gal</span>
                    <span className="text-right tabular-nums">{medida(datos?.ProductHeight)} mm</span>
                    <span className="text-right tabular-nums">{galones(datos?.ProductUllage)} gal</span>
                    <span className="text-right tabular-nums">{medida(datos?.Temperature)} °C</span>
                    <span className="text-right tabular-nums">
                      {alturaAgua > 0 ? `${medida(alturaAgua)} mm` : 'N/A'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
